import { useEffect, useState, useRef } from "react";
import {
  Box, Typography, Grid, Card, Avatar, Button, Chip, Stack, Skeleton, Tooltip, IconButton, Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import WorkIcon from "@mui/icons-material/Work";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupsIcon from "@mui/icons-material/Groups";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import BoltIcon from "@mui/icons-material/Bolt";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCurrentUser } from "../utils/useCurrentUser";
import { useFavoritesContext } from "../utils/FavoritesContext";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─── Solid colour stat card (original style, modernised) ─────────────────────
function StatCard({ icon, title, value, bgColor, onClick }) {
  return (
    <Card
      onClick={onClick}
      elevation={2}
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.75,
        bgcolor: bgColor,
        color: "#fff",
        borderRadius: 3,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": onClick ? { transform: "translateY(-3px)", boxShadow: 6 } : {},
        minHeight: 72,
        width: "100%",
      }}
    >
      <Avatar sx={{ bgcolor: "rgba(255,255,255,0.22)", mr: 2, width: 42, height: 42 }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="body2" sx={{ opacity: 0.88, fontWeight: 500, fontSize: "0.82rem" }}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, mt: 0.25 }}>
          {value ?? <Skeleton width={40} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />}
        </Typography>
      </Box>
    </Card>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <Typography sx={{
      fontSize: "0.68rem", fontWeight: 700, color: "#6b7280",
      textTransform: "uppercase", letterSpacing: "0.07em",
      mb: 1, mt: 0,
    }}>
      {children}
    </Typography>
  );
}

// ─── Leave Balance Card ───────────────────────────────────────────────────────
function LeaveBalanceCard({ leaveBalance, loading }) {
  if (loading || leaveBalance == null) {
    return (
      <Card elevation={2} sx={{
        display: "flex", alignItems: "center",
        p: 1.75, bgcolor: "#00838f", color: "#fff",
        borderRadius: 3, minHeight: 72, width: "100%",
      }}>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.22)", mr: 2, width: 42, height: 42 }}>
          <BeachAccessIcon />
        </Avatar>
        <Box flex={1}>
          <Skeleton width={100} height={14} sx={{ bgcolor: "rgba(255,255,255,0.3)", mb: 0.5 }} />
          <Skeleton width={60} height={28} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
        </Box>
      </Card>
    );
  }

  const {
    usedLeaves       = 0,
    remainingLeaves  = 0,
    quarterLabel     = "",
    quarterUsed      = 0,
    quarterRemaining = 0,
  } = leaveBalance;

  // Coerce to numbers — PostgreSQL SUM() returns numeric strings
  const used       = parseFloat(usedLeaves)       || 0;
  const remaining  = parseFloat(remainingLeaves)  || 0;
  const qUsed      = parseFloat(quarterUsed)      || 0;
  const qRemaining = parseFloat(quarterRemaining) || 0;
  // Full year = 12 paid leaves, full quarter = 3 — these are fixed entitlements
  const YEAR_TOTAL    = 12;
  const QUARTER_TOTAL = 3;

  const isLow = remaining <= 1;

  const tooltipContent = (
    <Box sx={{ p: 0.5, minWidth: 210 }}>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 1, color: "#fff" }}>
        Leave Balance Breakdown
      </Typography>

      {/* Year-to-date */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.7)", mb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Year to Date
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.85)" }}>Earned so far</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#fff" }}>{YEAR_TOTAL} days</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.85)" }}>Used</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: used > 0 ? "#ffb74d" : "#fff" }}>{used} days</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25, pt: 0.5, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#fff" }}>Remaining</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: isLow ? "#ff5252" : "#a5d6a7" }}>{remaining} days</Typography>
        </Stack>
      </Box>

      {/* Current quarter */}
      <Box sx={{ pt: 0.75, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
        <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.7)", mb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {quarterLabel || "Current Quarter"}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.85)" }}>Earned this quarter</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#fff" }}>{QUARTER_TOTAL} days</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.85)" }}>Used this quarter</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: qUsed > 0 ? "#ffb74d" : "#fff" }}>{qUsed} days</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25, pt: 0.5, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#fff" }}>Quarter remaining</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: qRemaining === 0 ? "#ff5252" : "#a5d6a7" }}>{qRemaining} days</Typography>
        </Stack>
      </Box>

      <Typography sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.5)", mt: 1, fontStyle: "italic" }}>
        1 paid leave per month · 3 per quarter · 12 per year
      </Typography>
    </Box>
  );

  // Card matches StatCard exactly: flex row, avatar + title/value, minHeight 72
  return (
    <Tooltip title={tooltipContent} placement="right" arrow
      componentsProps={{ tooltip: { sx: { bgcolor: "#006064", maxWidth: 250, p: 1.5, borderRadius: 2 } } }}>
      <Card elevation={2} sx={{
        display: "flex", alignItems: "center",
        p: 1.75, bgcolor: isLow ? "#b71c1c" : "#00838f", color: "#fff",
        borderRadius: 3, minHeight: 72, width: "100%",
        cursor: "default",
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": { transform: "translateY(-3px)", boxShadow: 6 },
      }}>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.22)", mr: 2, width: 42, height: 42 }}>
          <BeachAccessIcon />
        </Avatar>
        <Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" sx={{ opacity: 0.88, fontWeight: 500, fontSize: "0.82rem" }}>
              Leave Balance
            </Typography>
            <InfoOutlinedIcon sx={{ fontSize: 13, opacity: 0.65 }} />
          </Box>
          <Box display="flex" alignItems="baseline" gap={0.5}>
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, mt: 0.25 }}>
              {remaining}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.8, fontWeight: 500 }}>
              days
            </Typography>
          </Box>
        </Box>
      </Card>
    </Tooltip>
  );
}

// ─── Holiday Calendar Card ────────────────────────────────────────────────────
function HolidayCalendarCard({ holidays, loading, onViewAll }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // upcoming = today or future, sorted ascending, max 4 shown
  const upcoming = holidays
    .filter(h => {
      const s = String(h.holiday_date || "");
      const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s.slice(0, 10);
      return new Date(dateStr + "T00:00:00") >= today;
    })
    .slice(0, 4);

  const fmtShort = (raw) => {
    const s = String(raw || "");
    const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s.slice(0, 10);
    const dt = new Date(dateStr + "T00:00:00");
    return {
      day:   dt.toLocaleDateString("en-US", { weekday: "short" }),
      date:  dt.getDate(),
      month: dt.toLocaleDateString("en-US", { month: "short" }),
      isToday: dt.getTime() === today.getTime(),
    };
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": { transform: "translateY(-3px)", boxShadow: 6 },
        height: "100%",
      }}
      onClick={onViewAll}
    >
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.25,
        background: "linear-gradient(135deg, #00695c 0%, #00897b 100%)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CelebrationOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem" }}>
            Upcoming Holidays
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.72rem" }}>
            View all
          </Typography>
          <ArrowForwardIosIcon sx={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }} />
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ bgcolor: "#fff", px: 1.5, py: 1 }}>
        {loading ? (
          <Stack spacing={1} py={0.5}>
            {[1, 2, 3].map(i => <Skeleton key={i} height={36} sx={{ borderRadius: 1 }} />)}
          </Stack>
        ) : upcoming.length === 0 ? (
          <Box sx={{ py: 2.5, textAlign: "center" }}>
            <CelebrationOutlinedIcon sx={{ fontSize: 32, color: "#e0e0e0", mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
              No upcoming holidays
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0} divider={<Divider />}>
            {upcoming.map((h, i) => {
              const { day, date, month, isToday } = fmtShort(h.holiday_date);
              return (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  py: 0.9, px: 0.5,
                  bgcolor: isToday ? "#E8F5E9" : "transparent",
                  borderRadius: 1,
                }}>
                  {/* Date badge */}
                  <Box sx={{
                    minWidth: 42, height: 42, borderRadius: 1.5,
                    bgcolor: isToday ? "#00695c" : "#f0faf8",
                    border: `1px solid ${isToday ? "#00695c" : "#b2dfdb"}`,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <Typography sx={{
                      fontSize: "0.58rem", fontWeight: 700, lineHeight: 1,
                      color: isToday ? "rgba(255,255,255,0.85)" : "#00695c",
                      textTransform: "uppercase",
                    }}>
                      {month}
                    </Typography>
                    <Typography sx={{
                      fontSize: "1rem", fontWeight: 800, lineHeight: 1.1,
                      color: isToday ? "#fff" : "#00695c",
                    }}>
                      {date}
                    </Typography>
                    <Typography sx={{
                      fontSize: "0.55rem", fontWeight: 600, lineHeight: 1,
                      color: isToday ? "rgba(255,255,255,0.75)" : "#80cbc4",
                      textTransform: "uppercase",
                    }}>
                      {day}
                    </Typography>
                  </Box>

                  {/* Name + type */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: "0.82rem", fontWeight: 600, color: "#172B4D",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {h.holiday_name}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" mt={0.25}>
                      <Chip
                        label={h.holiday_type || "Full Day"}
                        size="small"
                        sx={{
                          height: 16, fontSize: "0.6rem", fontWeight: 700,
                          bgcolor: h.holiday_type === "Half Day" ? "#FFF0B3" : "#E3FCEF",
                          color:   h.holiday_type === "Half Day" ? "#FF8B00" : "#006644",
                        }}
                      />
                      {isToday && (
                        <Chip label="Today" size="small"
                          sx={{ height: 16, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#00695c", color: "#fff" }} />
                      )}
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Card>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [dashboardData, setDashboardData]   = useState(null);
  const [leaveBalance, setLeaveBalance]     = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [holidays, setHolidays]             = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(true);
  const [loading, setLoading]               = useState(true);
  const navigate  = useNavigate();
  const isMounted = useRef(true);
  const { empId, boId, userId } = useCurrentUser();
  const { favorites, isFavorite, toggleFavorite } = useFavoritesContext();

  useEffect(() => {
    isMounted.current = true;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/dashboard-data`, { credentials: "include" });
        if (res.status === 401 || res.status === 403) { navigate("/login", { replace: true }); return; }
        const data = await res.json();
        if (isMounted.current) setDashboardData(data);
      } catch { navigate("/login", { replace: true }); }
      finally { if (isMounted.current) setLoading(false); }
    })();
    return () => { isMounted.current = false; };
  }, [navigate]);

  useEffect(() => {
    if (!empId || !boId) return;
    fetch(`${BASE_URL}/api/Leave-Balance/${empId}?bo_id=${boId}`)
      .then(r => r.json())
      .then(d => { if (isMounted.current) setLeaveBalance(d); })
      .catch(console.error);
  }, [empId, boId]);

  useEffect(() => {
    if (!empId || !boId) return;
    fetch(`${BASE_URL}/api/users/${empId}/profile-picture?bo_id=${boId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.success && d.data?.profile_picture_url && isMounted.current) setProfilePicture(d.data.profile_picture_url); })
      .catch(console.error);
  }, [empId, boId]);

  // Fetch holidays for current year
  useEffect(() => {
    if (!boId) return;
    const year = new Date().getFullYear();
    setHolidaysLoading(true);
    fetch(`${BASE_URL}/api/holidays?bo_id=${boId}&year=${year}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (isMounted.current) setHolidays(Array.isArray(data) ? data : data.data || []); })
      .catch(() => { if (isMounted.current) setHolidays([]); })
      .finally(() => { if (isMounted.current) setHolidaysLoading(false); });
  }, [boId]);

  const d = dashboardData;
  const now     = new Date();
  const timeStr = now.toLocaleTimeString("en-IN",   { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-IN",   { timeZone: "Asia/Kolkata", weekday: "long", year: "numeric", month: "long", day: "numeric" });

  const tsAction    = parseInt(d?.timesheets_pending_my_action) || 0;
  const lvAction    = parseInt(d?.leaves_pending_my_action)     || 0;
  const totalAction = tsAction + lvAction;

  // Derive upcoming holiday count from the already-fetched holidays array.
  // "Upcoming" = today or in the future (same logic used by HolidayCalendarCard).
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const upcomingHolidaysCount = holidaysLoading ? null : holidays.filter(h => {
    const s = String(h.holiday_date || "");
    const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s.slice(0, 10);
    return new Date(dateStr + "T00:00:00") >= todayMidnight;
  }).length;

  return (
    <Box sx={{
      position: "fixed",
      top: "64px",
      left: "100px",
      right: 0,
      bottom: 0,
      overflow: "auto",
      p: { xs: 2, sm: 2.5 },
      bgcolor: "#f5f7fa",
    }}>
      {/* ── Welcome banner ── */}
      <Card elevation={3} sx={{
        mb: 1.5, px: 3, py: 1.5,
        borderRadius: 3,
        background: "linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%)",
        color: "#fff",
        flexShrink: 0,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              src={profilePicture || undefined}
              sx={{ width: 54, height: 54, bgcolor: "rgba(255,255,255,0.25)", fontSize: 22, fontWeight: 700, border: "2px solid rgba(255,255,255,0.4)" }}
            >
              {!profilePicture && (d?.full_name?.[0]?.toUpperCase() || "U")}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: "0.72rem", opacity: 0.75 }}>{dateStr}</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", lineHeight: 1.3 }}>
                {loading ? <Skeleton width={200} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} /> : `Welcome back, ${d?.full_name || "User"}`}
              </Typography>
              <Stack direction="row" spacing={2} mt={0.4}>
                <Typography sx={{ fontSize: "0.72rem", opacity: 0.75 }}>ID: <strong>{d?.emp_id || "—"}</strong></Typography>
                <Typography sx={{ fontSize: "0.72rem", opacity: 0.75 }}>Org: <strong>{d?.bo_id || "—"}</strong></Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
              <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, lineHeight: 1 }}>{timeStr}</Typography>
              <Typography sx={{ fontSize: "0.68rem", opacity: 0.65 }}>IST</Typography>
            </Box>
            <Button variant="contained" startIcon={<EventAvailableOutlinedIcon />}
              onClick={() => navigate("/employeeattendance")}
              sx={{ bgcolor: "rgba(255,255,255,0.18)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" }, border: "1px solid rgba(255,255,255,0.35)", borderRadius: 2, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", boxShadow: "none" }}>
              Check-In / Out
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* ── Approval alert banner ── */}
      {!loading && totalAction > 0 && (
        <Card elevation={0} sx={{ mb: 1.5, px: 2.5, py: 1.25, borderRadius: 3, bgcolor: "#fff3e0", border: "1px solid #ffb74d" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <NotificationsActiveIcon sx={{ color: "#f57c00", fontSize: 26 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#e65100", fontSize: "0.92rem" }}>
                  {totalAction} item{totalAction > 1 ? "s" : ""} need{totalAction === 1 ? "s" : ""} your approval
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#bf360c" }}>
                  {tsAction > 0 && `${tsAction} timesheet${tsAction > 1 ? "s" : ""}`}
                  {tsAction > 0 && lvAction > 0 && " · "}
                  {lvAction > 0 && `${lvAction} leave request${lvAction > 1 ? "s" : ""}`}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              {tsAction > 0 && (
                <Button size="small" variant="contained" onClick={() => navigate("/timesheettracker")}
                  sx={{ bgcolor: "#f57c00", "&:hover": { bgcolor: "#e65100" }, textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: "0.78rem" }}>
                  Review Timesheets
                </Button>
              )}
              {lvAction > 0 && (
                <Button size="small" variant="contained" onClick={() => navigate("/leaveapproval")}
                  sx={{ bgcolor: "#f57c00", "&:hover": { bgcolor: "#e65100" }, textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: "0.78rem" }}>
                  Review Leaves
                </Button>
              )}
            </Stack>
          </Stack>
        </Card>
      )}

      {/* ── Main two-column layout ── */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>

        {/* ── LEFT: stat cards ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Quick Access */}
          {favorites.length > 0 && (
            <>
              <SectionLabel>Quick Access</SectionLabel>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                {favorites.map((fav) => (
                  <Tooltip key={fav.screen_path} title={`Go to ${fav.screen_name}`}>
                    <Card elevation={1}
                      sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, borderRadius: 2, cursor: "pointer", border: "1px solid #e8e8e8", transition: "all 0.15s ease", "&:hover": { boxShadow: 3, borderColor: "#2DB0E4", transform: "translateY(-2px)" }, position: "relative" }}
                      onClick={() => navigate(fav.screen_path)}>
                      <BoltIcon sx={{ fontSize: 15, color: "#f59e0b" }} />
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#333", whiteSpace: "nowrap" }}>{fav.screen_name}</Typography>
                      <Tooltip title="Remove from Quick Access">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleFavorite(fav.screen_path, fav.screen_name); }}
                          sx={{ padding: "2px", ml: 0.5, color: "#f59e0b", "&:hover": { color: "#d97706", backgroundColor: "transparent" } }}>
                          <StarIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Card>
                  </Tooltip>
                ))}
              </Box>
            </>
          )}

          {/* My Activity */}
          <SectionLabel>My Activity</SectionLabel>
          <Grid container spacing={1.5} mb={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<ScheduleIcon />} title="Timesheets In Progress"
                value={loading ? null : (d?.pending_timesheets ?? 0)} bgColor="#f57c00"
                onClick={() => navigate("/timesheet")} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<AssignmentTurnedInIcon />} title="Timesheets Approved"
                value={loading ? null : (d?.approved_timesheets ?? 0)} bgColor="#2e7d32"
                onClick={() => navigate("/timesheet")} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<AccessTimeIcon />} title="Leave Requests Pending"
                value={loading ? null : (d?.pending_leaves ?? 0)} bgColor="#6a1b9a"
                onClick={() => navigate("/leaverequest")} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LeaveBalanceCard leaveBalance={leaveBalance} loading={loading || leaveBalance == null} />
            </Grid>
          </Grid>

          {/* Pending My Approval */}
          {!loading && totalAction > 0 && (
            <>
              <SectionLabel>Pending My Approval</SectionLabel>
              <Grid container spacing={1.5} mb={1.5}>
                {tsAction > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<PendingActionsIcon />} title="Timesheets Awaiting My Action"
                      value={tsAction} bgColor="#c62828" onClick={() => navigate("/timesheettracker")} />
                  </Grid>
                )}
                {lvAction > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<HowToRegIcon />} title="Leave Requests Awaiting My Action"
                      value={lvAction} bgColor="#e65100" onClick={() => navigate("/leaveapproval")} />
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* Organisation */}
          <SectionLabel>Organisation</SectionLabel>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard icon={<WorkIcon />} title="My Projects"
                value={loading ? null : (d?.total_projects ?? 0)} bgColor="#1565c0"
                onClick={() => navigate("/clientprojects")} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard icon={<GroupsIcon />} title="Total Employees"
                value={loading ? null : (d?.total_employees ?? 0)} bgColor="#ad1457"
                onClick={() => navigate("/employeedetails")} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard icon={<CalendarTodayIcon />} title="Upcoming Holidays"
                value={upcomingHolidaysCount}
                bgColor="#00695c"
                onClick={() => navigate("/holidays")} />
            </Grid>
          </Grid>
        </Box>

        {/* ── RIGHT: Holiday calendar panel ── */}
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <HolidayCalendarCard
            holidays={holidays}
            loading={holidaysLoading}
            onViewAll={() => navigate("/holidays")}
          />
        </Box>

      </Box>
    </Box>
  );
}
